/**
 * A station's board in words (the station workspaces plan, step 5): the watch list joined to the levels, one row per
 * tag, and every sentence a tile says. Pure: the server judges every level (platform-core/internal/twin/
 * stations_levels.go, held to testdata/station_level_vectors.json), and this module only says what it judged.
 *
 * - A level row's value, band and slope are in that row's own unit, already the org's display unit: nothing here
 *   converts, so a number is never converted twice (keepUnit).
 * - Reasons, in plain sentences (docs/design/2026-10-23_writing-standard.md): a supported driver is "The data supports
 *   a link to cold shut", a suggestive one "A lead for cold shut, not proven", never the stronger words. A stake reads
 *   its cost and currency where the org priced a casting, else about its whole pieces, and joins the reason in one
 *   sentence (watchSentence), never glued to it with a full stop.
 * - Times: a reading known only to its day reads "as of <day>"; one timed by its entry, "entered <time>"; an observed
 *   one, its age on a live board and "as of <time>" otherwise. A trend's edge is reached `reaches_edge_in_min` after the
 *   levels' as_of, so it is said as a clock time, which stays right between two reads.
 */
import {
  pinsSavedUnread,
  type StationLevel,
  type StationLevels,
  type WatchItem,
  type WatchPin,
  type WatchPinPut,
  type WatchStake,
} from "@/lib/api/station-board";
import { errorCodeOf, errorDetailOf, statusOf } from "@/lib/api/error";
import { codeWords } from "./kind-labels";
import type { Station } from "@/lib/api/stations";
import { stationLineLabel } from "./station-creator";
import { dimensionOf } from "@/lib/units/convert";
import { formatQuantity } from "@/lib/units/format";
import type { DisplayPrefs } from "@/lib/units/prefs";
import { inline } from "@/lib/brief/words";
import { withProbeWords } from "@/lib/brief/param-words";

/** Prefs that show a value in the unit it arrived in: the server already chose the org's display unit. */
function keepUnit(unit: string): DisplayPrefs {
  const dim = dimensionOf(unit);
  return { system: "si", overrides: dim ? { [dim]: unit } : {} } as DisplayPrefs;
}

/** The most places a board's number is given. */
const MAX_PLACES = 4;

/** A number in a row's own unit, rounded as the unit is read ("1425 °C", "0.35 %"), or to `places` when given. */
export function quantityText(value: number, unit: string, places?: number): string {
  return formatQuantity(value, unit, keepUnit(unit), places === undefined ? {} : { decimals: places }).text;
}

/** The number part of a quantity's text. */
const numberPart = (text: string) => text.split(" ")[0];

/** The places a written number carries ("1420.50" carries 2). */
const placesIn = (text: string) => /\.(\d+)$/.exec(numberPart(text))?.[1].length ?? 0;

/** The places a number needs to be written exactly, to at most MAX_PLACES (1420.5 needs 1). */
function placesOf(n: number): number {
  const s = String(Number(n.toFixed(MAX_PLACES)));
  const dot = s.indexOf(".");
  return dot < 0 ? 0 : s.length - dot - 1;
}

/** A window's end in the row's unit, with the places the plant set it to (a 1420.5 floor never reads 1421). */
function bandEndText(value: number, unit: string): string {
  const places = Math.max(placesIn(quantityText(value, unit)), placesOf(value));
  return numberPart(quantityText(value, unit, places));
}

/**
 * A rate in the row's unit per hour, to at least two significant figures: a 0.3 °C/h drift reads 0.3, never 0, as a
 * temperature's whole degrees would round it.
 */
function rateText(value: number, unit: string): string {
  const a = Math.abs(value);
  const places = a === 0 || a >= 10 ? 0 : a >= 1 ? 1 : Math.min(MAX_PLACES + 2, Math.ceil(-Math.log10(a)) + 1);
  // Its figures, without the zeros a fixed count of places would trail ("2", never "2.0").
  return String(Number(numberPart(quantityText(value, unit, places))));
}

/** One line of the board: a watched condition and one tag that carries it (or none). */
export interface BoardRow {
  key: string;
  conditionKey: string;
  label: string;
  /** The watch list's item; absent for a level whose condition joined the list after it was read. */
  watch?: WatchItem;
  /** The tag's level; absent while the levels are read, or when they are hidden or failed. */
  level?: StationLevel;
}

/** A row's React key: its condition and its tag (a tag id is a uuid, so the pair cannot collide). */
const rowKey = (condition: string, l: StationLevel, i: number) => `${condition} ${l.tag_id || `#${i}`}`;

/**
 * The watch list joined to the levels by condition_key, in the list's order: one row per tag that carries a condition
 * (the levels' order within it), one row with no level while the levels are absent. A level of a condition the list
 * read before did not hold (the two reads are a moment apart) is kept, after the list's own.
 */
export function boardRows(items: readonly WatchItem[], levels: readonly StationLevel[] | undefined): BoardRow[] {
  const byKey = new Map<string, StationLevel[]>();
  for (const l of levels ?? []) {
    const list = byKey.get(l.condition_key);
    if (list) list.push(l);
    else byKey.set(l.condition_key, [l]);
  }
  const rows: BoardRow[] = [];
  const seen = new Set<string>();
  for (const w of items) {
    seen.add(w.condition_key);
    const ls = byKey.get(w.condition_key);
    if (!ls || ls.length === 0) {
      rows.push({ key: w.condition_key, conditionKey: w.condition_key, label: w.label, watch: w });
      continue;
    }
    ls.forEach((l, i) =>
      rows.push({ key: rowKey(w.condition_key, l, i), conditionKey: w.condition_key, label: w.label, watch: w, level: l }),
    );
  }
  for (const [key, ls] of byKey) {
    if (seen.has(key)) continue;
    ls.forEach((l, i) => rows.push({ key: rowKey(key, l, i), conditionKey: key, label: l.label, level: l }));
  }
  return rows;
}

/** How a level is shown: its words and its tone (the badge's colour). */
export interface LevelLook {
  word: string;
  tone: "success" | "warning" | "danger" | "secondary";
}

/** A level in words, with the reason the server gave. */
export function levelLook(l: Pick<StationLevel, "level" | "why" | "trend">): LevelLook {
  switch (l.level) {
    case "ok":
      return { word: "In window", tone: "success" };
    case "stop":
      // Outside the stop limit the plant set (step 4b): the hardest level, judged before the window.
      return { word: l.why === "above_stop" ? "Stop: above the stop limit" : "Stop: below the stop limit", tone: "danger" };
    case "warn":
      return { word: l.why === "above" ? "Above the window" : "Below the window", tone: "danger" };
    case "watch":
      if (l.why === "trend") {
        const up = (l.trend?.slope_per_h ?? 0) > 0;
        return { word: up ? "Heading for the top" : "Heading for the bottom", tone: "warning" };
      }
      return { word: "Near the edge", tone: "warning" };
    default:
      return { word: NONE_WORDS[l.why ?? ""] ?? "Not judged", tone: "secondary" };
  }
}

const NONE_WORDS: Record<string, string> = {
  no_window: "No plant window",
  no_value: "No reading",
  no_tag: "No tag on this station",
  unit_mismatch: "Reading in another unit",
};

/** Why a condition is on the list, as a sentence starts: how sure the data is of a link to its defect (the defect in the
 *  plant's own word, lowered as a sentence lowers it: inline), a plant window, or a pin. */
export function reasonText(w: Pick<WatchItem, "reason" | "verdict" | "defect" | "defect_label">): string {
  const defect = (w.defect_label || w.defect || "").trim();
  const named = defect ? inline(defect) : "";
  switch (w.reason) {
    case "driver":
      if (w.verdict === "supported") return named ? `The data supports a link to ${named}` : "The data supports a link";
      if (w.verdict === "suggestive") return named ? `A lead for ${named}, not proven` : "A lead, not proven";
      return "The plant brief names it";
    case "window":
      return "The plant set a window for it";
    case "pinned":
      return "Pinned to the list";
  }
}

/** The watch line as one sentence: why it is watched, then what it puts at stake ("The data supports a link to cold
 *  shut, with about 4,770 pieces at stake."). */
export function watchSentence(w: Pick<WatchItem, "reason" | "verdict" | "defect" | "defect_label" | "stake">): string {
  const stake = stakeText(w.stake);
  return `${reasonText(w)}${stake ? `, with ${stake}` : ""}.`;
}

/** Where a plant-wide condition is carried, as a sentence: "It is on 2 machines.", "No machine carries it." */
export function carriedText(machines: number): string {
  if (machines <= 0) return "No machine carries it.";
  return `It is on ${machines.toLocaleString()} ${machines === 1 ? "machine" : "machines"}.`;
}

/** A sum of money in its currency, whole: the ISO code's own symbol where the browser knows it. */
export function moneyText(value: number, currency: string): string {
  const code = currency.trim();
  try {
    return value.toLocaleString(undefined, { style: "currency", currency: code, maximumFractionDigits: 0 });
  } catch {
    return `${Math.round(value).toLocaleString()} ${code}`;
  }
}

/** What a condition puts at stake: its cost where the org priced a casting, else about its whole pieces (an estimate);
 *  null for none. */
export function stakeText(s: WatchStake | undefined): string | null {
  if (!s) return null;
  if (s.cost !== undefined && Number.isFinite(s.cost) && s.cost > 0 && s.currency?.trim()) {
    return `${moneyText(s.cost, s.currency)} at stake`;
  }
  const n = Math.round(s.pieces);
  if (!Number.isFinite(n) || n <= 0) return null;
  return `about ${n.toLocaleString()} ${n === 1 ? "piece" : "pieces"} at stake`;
}

/**
 * A level's value with its unit, or what stands in its place. It carries the places its window's ends do, and more
 * where rounding would put it on the other side of an end, or on an end it is not on: 1420.6 against a 1421 floor reads
 * "1420.6 °C", never "1421 °C" beside "Below the window".
 */
export function valueText(l: Pick<StationLevel, "value" | "unit"> & { band?: StationLevel["band"] }): string {
  if (l.value === null) return "No value";
  const v = l.value;
  // Every edge the value is judged against: the window's ends and the stop limit's (step 4b).
  const b = l.band ?? {};
  const edges = [b.lo, b.hi, b.stop_lo, b.stop_hi].filter((e): e is number => e !== undefined);
  const sameSides = (x: number) => edges.every((e) => Math.sign(x - e) === Math.sign(v - e));
  let places = Math.max(placesIn(quantityText(v, l.unit)), ...edges.map(placesOf));
  while (places < MAX_PLACES) {
    if (sameSides(Number(numberPart(quantityText(v, l.unit, places))))) break;
    places++;
  }
  return quantityText(v, l.unit, places);
}

/**
 * The window a level is judged against: both ends, a floor, or a ceiling, in the row's unit, with the grade's name; and
 * the stop limit's ends that apply (step 4b), beyond it ("1380 to 1450 °C; stops below 1360 °C"). A stop applies with
 * no window too, so it may be said alone.
 */
export function bandText(l: Pick<StationLevel, "band" | "unit">): string | null {
  const { lo, hi, grade, stop_lo: slo, stop_hi: shi } = l.band;
  const u = l.unit ? ` ${l.unit}` : "";
  let text = "";
  if (lo !== undefined && hi !== undefined) text = `${bandEndText(lo, l.unit)} to ${bandEndText(hi, l.unit)}${u}`;
  else if (lo !== undefined) text = `at least ${bandEndText(lo, l.unit)}${u}`;
  else if (hi !== undefined) text = `at most ${bandEndText(hi, l.unit)}${u}`;
  if (text && grade) text = `${text}, the ${grade} window`;
  const stop =
    slo !== undefined && shi !== undefined
      ? `stops below ${bandEndText(slo, l.unit)} or above ${bandEndText(shi, l.unit)}${u}`
      : slo !== undefined
        ? `stops below ${bandEndText(slo, l.unit)}${u}`
        : shi !== undefined
          ? `stops above ${bandEndText(shi, l.unit)}${u}`
          : "";
  if (!text && !stop) return null;
  return text && stop ? `${text}; ${stop}` : text || stop;
}

/** A time on the shop floor's clock: the plant's zone when given, else the browser's. */
function clock(iso: string, zone: string | undefined, opts: Intl.DateTimeFormatOptions): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  try {
    return d.toLocaleString(undefined, zone ? { ...opts, timeZone: zone } : opts);
  } catch {
    return d.toLocaleString(undefined, opts);
  }
}

const DAY: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
const TIME: Intl.DateTimeFormatOptions = { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" };
const HOUR: Intl.DateTimeFormatOptions = { hour: "2-digit", minute: "2-digit" };

/** A moment on the shop floor's clock: its hour and minute, with its day when that is not asOf's ("14:07", "6 Oct, 14:07"). */
export function momentText(iso: string, asOf: string, zone?: string): string {
  return clock(iso, zone, clock(iso, zone, DAY) === clock(asOf, zone, DAY) ? HOUR : TIME);
}

/** How old a reading is at a moment: "just now", "12 min ago", "5 h ago", "41 days ago". */
export function ageWords(at: string, asOf: string): string {
  const minutes = Math.floor((Date.parse(asOf) - Date.parse(at)) / 60_000);
  if (!Number.isFinite(minutes)) return "";
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.floor(hours / 24)} days ago`;
}

/**
 * When a level's value is from: "as of <day>" for a reading known only to its day, "entered <time>" for one timed only
 * by its typing, its age on a live board, "as of <time>" otherwise. Null with no reading. A day is read in the plant's
 * zone, else in UTC (a plant with no zone keeps UTC days), never the browser's, which could name the day before.
 */
export function timeText(l: Pick<StationLevel, "at" | "at_basis">, board: Pick<StationLevels, "as_of" | "live">, zone?: string): string | null {
  if (!l.at) return null;
  if (l.at_basis === "day") return `as of ${clock(l.at, zone ?? "UTC", DAY)}`;
  if (l.at_basis === "entry") return `entered ${clock(l.at, zone, TIME)}`;
  return board.live ? ageWords(l.at, board.as_of) : `as of ${clock(l.at, zone, TIME)}`;
}

/** What the board says when a read failed and an older one stays on show: how old it is, on the plant's clock. */
export function staleWords(what: "list" | "values", at: string, zone?: string): string {
  return what === "list"
    ? `Reading the watch list again failed, so this one is as computed ${clock(at, zone, TIME)}.`
    : `Reading the values again failed, so these are as of ${clock(at, zone, TIME)}.`;
}

/** How far ahead a trend's edge is said as a clock time; past it, in hours ("in about 5 h"). */
export const TREND_CLOCK_WITHIN_MIN = 180;

/**
 * A level's trend: its direction and rate per hour, and when it reaches the edge it heads for, counted from the levels'
 * as_of: a clock time within three hours (with its date when that is not as_of's day), else in about so many hours.
 */
export function trendText(l: Pick<StationLevel, "trend" | "unit">, asOf: string, zone?: string): string | null {
  const t = l.trend;
  if (!t) return null;
  if (t.slope_per_h === 0) return "Steady";
  const rate = `${rateText(Math.abs(t.slope_per_h), l.unit)}${l.unit ? ` ${l.unit}` : ""}/h`;
  const head = `${t.slope_per_h > 0 ? "Rising" : "Falling"} ${rate}`;
  const m = t.reaches_edge_in_min;
  if (m === undefined) return head;
  const edge = `the ${t.slope_per_h > 0 ? "top" : "bottom"} of the window`;
  if (m >= TREND_CLOCK_WITHIN_MIN) return `${head}; at this rate it reaches ${edge} in about ${Math.round(m / 60)} h`;
  const at = new Date(Date.parse(asOf) + m * 60_000).toISOString();
  const sameDay = clock(at, zone, DAY) === clock(asOf, zone, DAY);
  return `${head}; reaches ${edge} around ${clock(at, zone, sameDay ? HOUR : TIME)}`;
}

/** The board's count of what needs a look: out of the window, and heading for or near its edge. */
export function boardCounts(rows: readonly BoardRow[]): { stop: number; warn: number; watch: number } {
  let stop = 0;
  let warn = 0;
  let watch = 0;
  for (const r of rows) {
    if (r.level?.level === "stop") stop++;
    else if (r.level?.level === "warn") warn++;
    else if (r.level?.level === "watch") watch++;
  }
  return { stop, warn, watch };
}

/**
 * The board's one line: what is outside its window and what to watch, else that everything judged is in, and whether
 * the values are live. "" when there is nothing to say.
 */
export function boardSummary(rows: readonly BoardRow[], levels?: Pick<StationLevels, "live">, stale = false): string {
  const { stop, warn, watch } = boardCounts(rows);
  const judged = rows.some((r) => r.level !== undefined && r.level.level !== "none");
  const parts: string[] = [];
  if (stop > 0) parts.push(`${stop} past a stop limit`);
  if (warn > 0) parts.push(`${warn} outside the window`);
  if (watch > 0) parts.push(`${watch} to watch`);
  if (stop === 0 && warn === 0 && watch === 0 && judged) parts.push("everything judged is in its window");
  if (levels) parts.push(stale ? "values from an earlier read" : levels.live ? "live" : "latest uploaded values, not live");
  const s = parts.join("; ");
  return s ? s[0].toUpperCase() + s.slice(1) : "";
}

/** The stations that hold an entity, in the org's order (the one-machine view's "In stations"). */
export function stationsHolding(stations: readonly Station[], entityId: string): Station[] {
  return stations.filter((s) => s.entities.includes(entityId));
}

/** A station's line, its machines and its people, in one sentence (the station view's header). */
export function stationDescription(s: Station): string {
  const machines = `${s.entities.length} ${s.entities.length === 1 ? "machine" : "machines"}`;
  const people = s.members.length
    ? s.members.map((m) => `${m.display_name || m.email || "a member"} (${m.role})`).join(", ")
    : "no one assigned yet";
  return `${stationLineLabel(s.line)}. ${machines}. People: ${people}.`;
}

/** A date in words ("Oct 7, 2026") on the plant's clock, else UTC's; "" when it does not read. */
export function dateText(iso: string, zone?: string): string {
  if (Number.isNaN(new Date(iso).getTime())) return "";
  return clock(iso, zone ?? "UTC", { month: "short", day: "numeric", year: "numeric" });
}

/** A condition's name: its label where the board holds one, else its key in words ("carbon_equivalent" reads
 * "Carbon equivalent"), for a condition pinned off the list, which the list no longer names. */
export function conditionName(key: string, labels: ReadonlyMap<string, string>): string {
  return labels.get(key) ?? withProbeWords(key, codeWords); // a second probe's id is never shown (#1696)
}

/** The most a pin's reason may say, as the server allows. */
export const PIN_WHY_MAX = 280;

/** The pins as the editor starts them: as stored. */
export function pinsDraftOf(pins: readonly WatchPin[]): WatchPinPut[] {
  return pins.map((p) => ({ condition_key: p.condition_key, pin: p.pin, why: p.why }));
}

/** What stops a set of pins being saved, in words; null when it may be. The server checks again (400 invalid_pins). */
export function pinsProblem(pins: readonly WatchPinPut[]): string | null {
  const keys = new Set<string>();
  for (const p of pins) {
    const key = p.condition_key.trim();
    if (!key) return "Each pin needs the condition it pins.";
    if (keys.has(key.toLowerCase())) return `"${key}" is pinned twice.`;
    keys.add(key.toLowerCase());
    if (!p.why.trim()) return `Say why "${key}" is pinned.`;
    if (p.why.trim().length > PIN_WHY_MAX) return `The reason for "${key}" is longer than ${PIN_WHY_MAX} characters.`;
  }
  return null;
}

/** What a save the server could not read back says: it was saved (the server's 5xx carries no detail to say it). */
export const PINS_SAVED_UNREAD = "The pins were saved. The list could not be read back just now, so it is being read again.";

/**
 * Why a pins write failed, at the form: that it was saved after all (pins_saved_read_failed), who may pin when the
 * caller may not, else the server's own sentence (an unknown
 * condition or a bad pin says which), else the status.
 */
export function pinsErrorWords(err: unknown): string {
  if (pinsSavedUnread(err)) return PINS_SAVED_UNREAD;
  if (errorCodeOf(err) === "cannot_pin") return "Only an org admin, or this station's lead or engineer, sets its pins.";
  const detail = errorDetailOf(err);
  if (detail) return detail;
  const status = statusOf(err);
  return status === undefined
    ? "The pins could not be saved: the platform did not answer. Try again."
    : `The pins could not be saved (HTTP ${status}). Try again.`;
}

/** The pins as the PUT sends them: trimmed. */
export function pinsBody(pins: readonly WatchPinPut[]): WatchPinPut[] {
  return pins.map((p) => ({ condition_key: p.condition_key.trim(), pin: p.pin, why: p.why.trim() }));
}
